import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Calendar, RefreshCw, Filter, FileText, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useBooking } from '../contexts/BookingContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import BookingDialog from './BookingDialog';
import MultiSlotBookingDialog from './MultiSlotBookingDialog';
import ErrorBoundary from './ErrorBoundary';
import { toast } from '../hooks/use-toast';

// Helper to generate time slots based on operating hours
// For overnight facilities (e.g., 5 AM to 2 AM):
// - Selected day shows: opening time → 11:30 PM (just before midnight)
// - Next day shows: 12:00 AM → closing time + normal opening hours
const generateTimeSlots = (startHour = 6, endHour = 23, endMinute = 0, startMinute = 0) => {
  const slots = [];
  const startTotal = startHour * 60 + startMinute;
  const endTotal = endHour * 60 + endMinute;

  const emit = (from, to) => {
    for (let t = from; t + 30 <= to; t += 30) {
      const h = Math.floor(t / 60) % 24;
      const m = t % 60;
      slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    }
  };

  if (endTotal <= startTotal) {
    // Overnight: early morning (00:00 -> close) then evening (open -> midnight)
    emit(0, endTotal);
    emit(startTotal, 24 * 60);
  } else {
    emit(startTotal, endTotal);
  }

  return slots;
};

const formatTime = (timeStr) => {
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
};

// Short format for mobile
const formatTimeShort = (timeStr) => {
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'P' : 'A';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour}:${minutes}${ampm}`;
};

// Short court names for mobile
const getShortCourtName = (courtId, courtName) => {
  const shortNames = {
    // SUFC - Simplified names
    'full': '11',
    'north': '9A',
    'south': '9B',
    'north-east': '7A',
    'north-west': '7B',
    'south-east': '7C',
    'south-west': '7D',
    '5aside': '5',
    // PicknPadel
    'padel_1': 'P1',
    'padel_2': 'P2',
    'pickle_1': 'Pk1',
    'pickle_2': 'Pk2',
    'pickle_3': 'Pk3',
    // Ferrohub (Pickleball courts 1-6)
    'court_1': 'C1',
    'court_2': 'C2',
    'court_3': 'C3',
    'court_4': 'C4',
    'court_5': 'C5',
    'court_6': 'C6',
    // Depot 18 - Football (similar structure to SUFC but with 4 5-asides)
    '5aside-ne': '5A',
    '5aside-nw': '5B',
    '5aside-se': '5C',
    '5aside-sw': '5D',
  };
  return shortNames[courtId] || courtName?.substring(0, 4) || courtId;
};

// Court group definitions for filtering
const getCourtGroups = (courts) => {
  const groups = {};
  
  courts.forEach(court => {
    let groupName = 'Other';
    const id = court.id.toLowerCase();
    const name = court.name?.toLowerCase() || '';
    
    if (id === 'full' || name.includes('full') || name.includes('11v11')) {
      groupName = 'Full';
    } else if (id.includes('north') || id.includes('south') || name.includes('9v9') || name.includes('half')) {
      if (!id.includes('east') && !id.includes('west')) {
        groupName = 'Halves';
      } else {
        groupName = 'Quarters';
      }
    } else if (name.includes('7v7') || name.includes('quarter')) {
      groupName = 'Quarters';
    } else if (id.includes('5aside') || name.includes('5v5') || name.includes('5-aside') || name.includes('5 aside')) {
      groupName = '5-Aside';
    } else if (id.includes('turf') || name.includes('turf') || name.includes('football') || name.includes('box cricket')) {
      groupName = 'Turf';
    } else if (id.includes('snooker') || name.includes('snooker')) {
      groupName = 'Snooker';
    } else if (id.includes('padel') || name.includes('padel')) {
      groupName = 'Padel';
    } else if (id.includes('pickle') || name.includes('pickle')) {
      groupName = 'Pickle';
    }
    
    if (!groups[groupName]) groups[groupName] = [];
    groups[groupName].push(court.id);
  });
  
  return groups;
};

const CalendarView = () => {
  const { fetchBookings, fetchBlockedSlots } = useBooking();
  const { facility, isReadOnlyRole, isVenueFinance } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [showMultiSlotDialog, setShowMultiSlotDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [slotAvailability, setSlotAvailability] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  
  // Drag selection state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [dragSection, setDragSection] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // Touch scroll prevention state
  const [touchStartY, setTouchStartY] = useState(null);
  const [touchStartTime, setTouchStartTime] = useState(null);
  const SCROLL_THRESHOLD = 10; // pixels moved to consider it a scroll
  const TAP_THRESHOLD = 200; // ms to consider it a tap vs scroll

  // Get courts from facility configuration
  const allCourts = facility?.courts?.filter(c => c.status === 'active') || [];
  const courtGroups = getCourtGroups(allCourts);
  
  // Filter courts based on active filters
  const courts = activeFilters.length > 0
    ? allCourts.filter(c => activeFilters.some(group => courtGroups[group]?.includes(c.id)))
    : allCourts;
  
  const sections = courts.map(c => c.id);
  const sectionNames = courts.reduce((acc, c) => ({ ...acc, [c.id]: c.name }), {});

  // Generate time slots based on facility operating hours
  const getTimeSlots = useCallback(() => {
    if (facility?.operating_hours) {
      const [sh, sm] = facility.operating_hours.start.split(':').map(Number);
      const [eh, em] = facility.operating_hours.end.split(':').map(Number);
      return generateTimeSlots(sh, eh, em, sm);
    }
    return generateTimeSlots(6, 23, 0, 0);
  }, [facility]);

  const TIME_SLOTS = getTimeSlots();

  // Toggle filter
  const toggleFilter = (groupName) => {
    setActiveFilters(prev => 
      prev.includes(groupName) 
        ? prev.filter(f => f !== groupName)
        : [...prev, groupName]
    );
  };

  // Load slot availability from API
  const loadSlotAvailability = useCallback(async (date) => {
    setLoading(true);
    try {
      // SEC-005: read-only venue roles get the sanitized occupancy grid (no PII / amounts).
      const data = isReadOnlyRole
        ? await api.getCalendarOccupancy(date)
        : await api.getSlotAvailability(date);
      setSlotAvailability(data);
    } catch (error) {
      console.error('Error loading slot availability:', error);
    } finally {
      setLoading(false);
    }
  }, [isReadOnlyRole]);

  // Load data on mount and date change
  useEffect(() => {
    loadSlotAvailability(selectedDate);
    // Read-only roles must not hit the bookings API — the grid already has everything.
    if (!isReadOnlyRole) {
      fetchBookings({ date: selectedDate });
      fetchBlockedSlots(selectedDate);
    }
  }, [selectedDate, loadSlotAvailability, fetchBookings, fetchBlockedSlots, isReadOnlyRole]);

  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSlotAvailability(null);
    setSelectedDate(newDate.toISOString().split('T')[0]);
  };

  const refreshData = () => {
    loadSlotAvailability(selectedDate);
  };

  const getSlotStatus = (date, time, section) => {
    if (!slotAvailability?.slots) {
      return { status: 'available', booking: null };
    }

    const slotData = slotAvailability.slots.find(s => s.time === time);
    if (!slotData || !slotData.sections[section]) {
      return { status: 'available', booking: null };
    }

    const sectionData = slotData.sections[section];
    
    if (sectionData.blocked) {
      return { status: 'blocked', booking: null, reason: sectionData.block_reason };
    }

    if (!sectionData.available && sectionData.booking) {
      const booking = sectionData.booking;
      
      return {
        status: booking.status || 'confirmed',
        booking: {
          ...booking,
          customerName: booking.customer_name,
          customerPhone: booking.customer_phone,
          customerEmail: booking.customer_email,
          slotType: booking.slot_type,
          startTime: booking.start_time,
          endTime: booking.end_time,
          bookingType: booking.booking_type,
          paymentStatus: booking.payment_status,
          amount: booking.amount,
          amountPaid: booking.amount_paid,
          amountPending: booking.amount_pending,
          source: booking.source,
          paymentLinkSent: booking.payment_link_sent,
          paymentLinkUrl: booking.payment_link_url
        },
        isBlockedByOther: !booking.is_direct
      };
    }

    return { status: 'available', booking: null };
  };

  const getStatusColor = (status, booking, isInDragSelection, isBlockedByOther) => {
    if (isInDragSelection) {
      return 'bg-blue-200 border-blue-400';
    }
    
    if (isBlockedByOther) {
      return 'bg-gray-300 border-gray-400';
    }
    
    if (booking && (status === 'confirmed' || status === 'pending')) {
      if (booking.bookingType === 'event') {
        return 'bg-purple-100 border-purple-400';
      } else if (booking.bookingType === 'b2b') {
        return 'bg-orange-100 border-orange-400';
      } else {
        return status === 'confirmed' ? 'bg-blue-100 border-blue-300' : 'bg-yellow-100 border-yellow-300';
      }
    }
    
    switch (status) {
      case 'available':
        return 'bg-green-100 hover:bg-green-200 border-green-300';
      case 'confirmed':
        return 'bg-blue-100 border-blue-300';
      case 'pending':
        return 'bg-yellow-100 border-yellow-300';
      case 'blocked':
        return 'bg-red-100 border-red-300';
      default:
        return 'bg-gray-100 border-gray-300';
    }
  };

  const handleSlotMouseDown = (date, time, section, status) => {
    if (isReadOnlyRole) return; // view-only
    if (status === 'available') {
      setIsDragging(true);
      setDragStart(time);
      setDragEnd(time);
      setDragSection(section);
    } else {
      const slotInfo = getSlotStatus(date, time, section);
      setSelectedSlot({ date, time, section, ...slotInfo });
      setShowBookingDialog(true);
    }
  };

  // Touch handlers with scroll detection
  const handleTouchStart = (e, date, time, section, status) => {
    setTouchStartY(e.touches[0].clientY);
    setTouchStartTime(Date.now());
  };

  const handleTouchEnd = (e, date, time, section, status) => {
    if (isReadOnlyRole) return; // view-only
    if (touchStartY === null || touchStartTime === null) return;
    
    const touchEndY = e.changedTouches[0].clientY;
    const deltaY = Math.abs(touchEndY - touchStartY);
    const deltaTime = Date.now() - touchStartTime;
    
    // Only trigger click if it's a tap (not a scroll)
    if (deltaY < SCROLL_THRESHOLD && deltaTime < TAP_THRESHOLD) {
      if (status === 'available') {
        const slotInfo = getSlotStatus(date, time, section);
        setSelectedSlot({ date, time, section, ...slotInfo });
        setShowBookingDialog(true);
      } else {
        const slotInfo = getSlotStatus(date, time, section);
        setSelectedSlot({ date, time, section, ...slotInfo });
        setShowBookingDialog(true);
      }
    }
    
    setTouchStartY(null);
    setTouchStartTime(null);
  };

  const handleSlotMouseEnter = (time, section) => {
    if (isDragging && section === dragSection) {
      setDragEnd(time);
    }
  };

  const handleSlotMouseUp = (date, time, section, status) => {
    if (isReadOnlyRole) return; // view-only
    if (isDragging && dragStart && section === dragSection) {
      const startIndex = TIME_SLOTS.indexOf(dragStart);
      const endIndex = TIME_SLOTS.indexOf(dragEnd || time);
      
      const minIndex = Math.min(startIndex, endIndex);
      const maxIndex = Math.max(startIndex, endIndex);
      
      const slotsInRange = TIME_SLOTS.slice(minIndex, maxIndex + 1);
      
      const allAvailable = slotsInRange.every(slotTime => {
        const slotStatus = getSlotStatus(date, slotTime, section);
        return slotStatus.status === 'available';
      });
      
      if (allAvailable && slotsInRange.length > 1) {
        // Calculate actual end time by adding 30 minutes to the last slot
        const lastSlot = TIME_SLOTS[maxIndex];
        const [lastHour, lastMin] = lastSlot.split(':').map(Number);
        let endHour = lastHour;
        let endMin = lastMin + 30;
        if (endMin >= 60) {
          endMin -= 60;
          endHour += 1;
          if (endHour >= 24) {
            endHour = 0; // Midnight
          }
        }
        const calculatedEndTime = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
        
        setSelectedSlots({
          date,
          section,
          startTime: TIME_SLOTS[minIndex],
          endTime: calculatedEndTime,
          slots: slotsInRange
        });
        setShowMultiSlotDialog(true);
      } else if (slotsInRange.length === 1 || dragStart === (dragEnd || time)) {
        const slotInfo = getSlotStatus(date, dragStart, section);
        setSelectedSlot({ date, time: dragStart, section, ...slotInfo });
        setShowBookingDialog(true);
      }
      
      setIsDragging(false);
      setDragStart(null);
      setDragEnd(null);
      setDragSection(null);
    } else if (!isDragging && status !== 'available') {
      const slotInfo = getSlotStatus(date, time, section);
      setSelectedSlot({ date, time, section, ...slotInfo });
      setShowBookingDialog(true);
    }
  };

  const isSlotInDragSelection = (time, section) => {
    if (!isDragging || !dragStart || section !== dragSection) return false;
    if (!dragEnd) return time === dragStart;
    
    const startIndex = TIME_SLOTS.indexOf(dragStart);
    const endIndex = TIME_SLOTS.indexOf(dragEnd);
    const minIndex = Math.min(startIndex, endIndex);
    const maxIndex = Math.max(startIndex, endIndex);
    const timeIndex = TIME_SLOTS.indexOf(time);
    
    return timeIndex >= minIndex && timeIndex <= maxIndex;
  };

  const handleDialogClose = () => {
    setShowBookingDialog(false);
    setShowMultiSlotDialog(false);
    setSelectedSlot(null);
    setSelectedSlots([]);
    loadSlotAvailability(selectedDate);
  };

  // Download daily booking report PDF
  const handleDownloadPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/daily-pdf?date=${selectedDate}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bookings_${selectedDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "PDF Downloaded",
        description: `Daily report for ${new Date(selectedDate).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })} is ready.`
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        title: "Error",
        description: "Failed to generate PDF report",
        variant: "destructive"
      });
    }
  };

  // Get facility-specific accent color
  const accentColor = facility?.sports?.includes('padel') || facility?.sports?.includes('pickle') ? 'emerald' : 'blue';

  return (
    <div className="p-2 sm:p-6 pt-14 lg:pt-6 pb-32 lg:pb-6">
      <Card>
        <CardHeader className="p-3 sm:p-6">
          {/* Mobile Header */}
          <div className="lg:hidden space-y-3">
            {/* Date and Navigation */}
            <div className="flex items-center justify-between">
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => changeDate(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" className="text-xs px-2" onClick={() => setShowDatePicker(true)}>
                <Calendar className="h-3 w-3 mr-1" />
                {new Date(selectedDate).toLocaleDateString('en-IN', { 
                  weekday: 'short', 
                  day: 'numeric', 
                  month: 'short' 
                })}
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => changeDate(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={refreshData} disabled={loading}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              {!isVenueFinance && (
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleDownloadPDF} title="Download Daily Report PDF">
                <FileText className="h-4 w-4" />
              </Button>
              )}
            </div>
            
            {/* Court Filters - Mobile */}
            {Object.keys(courtGroups).length > 1 && (
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(courtGroups).map(([groupName, courtIds]) => (
                  <button
                    key={groupName}
                    onClick={() => toggleFilter(groupName)}
                    className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                      activeFilters.length === 0 || activeFilters.includes(groupName)
                        ? `bg-${accentColor}-100 text-${accentColor}-700 border border-${accentColor}-300`
                        : 'bg-gray-100 text-gray-400 border border-gray-200'
                    }`}
                  >
                    {groupName} ({courtIds.length})
                  </button>
                ))}
                {activeFilters.length > 0 && (
                  <button
                    onClick={() => setActiveFilters([])}
                    className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600"
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Desktop Header */}
          <div className="hidden lg:block">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
              <div>
                <CardTitle className="text-2xl">Booking Calendar</CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  {facility?.name && <span className={`font-semibold text-${accentColor}-600`}>{facility.name} • </span>}
                  {new Date(selectedDate).toLocaleDateString('en-IN', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
                <p className={`text-xs text-${accentColor}-600 font-semibold mt-1`}>
                  {isReadOnlyRole
                    ? '👁️ View-only calendar — booked and blocked slots for your venue'
                    : '💡 Click to view/edit bookings • Click and drag on available slots to create multi-slot bookings'}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="icon" onClick={() => changeDate(-1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" onClick={() => setShowDatePicker(true)} className="min-w-[140px]">
                  <Calendar className="h-4 w-4 mr-2" />
                  {new Date(selectedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </Button>
                <Button variant="outline" size="icon" onClick={() => changeDate(1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={refreshData} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
                {!isVenueFinance && (
                <Button variant="outline" onClick={handleDownloadPDF} className="ml-2">
                  <FileText className="h-4 w-4 mr-2" />
                  Daily Report PDF
                </Button>
                )}
              </div>
            </div>
            
            {/* Court Filters - Desktop */}
            {Object.keys(courtGroups).length > 1 && (
              <div className="flex flex-wrap gap-2 mt-4">
                <span className="text-sm text-gray-500 mr-2 flex items-center">
                  <Filter className="h-4 w-4 mr-1" /> Filter:
                </span>
                {Object.entries(courtGroups).map(([groupName, courtIds]) => (
                  <button
                    key={groupName}
                    onClick={() => toggleFilter(groupName)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      activeFilters.length === 0 || activeFilters.includes(groupName)
                        ? `bg-${accentColor}-100 text-${accentColor}-700 border border-${accentColor}-300`
                        : 'bg-gray-100 text-gray-400 border border-gray-200'
                    }`}
                  >
                    {groupName} ({courtIds.length})
                  </button>
                ))}
                {activeFilters.length > 0 && (
                  <button
                    onClick={() => setActiveFilters([])}
                    className="px-3 py-1 rounded-full text-sm font-medium bg-gray-200 text-gray-600 hover:bg-gray-300"
                  >
                    Show All
                  </button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-2 sm:p-6 pt-0">
          {/* Legend - Compact on mobile */}
          <div className="flex flex-wrap gap-2 sm:gap-4 mb-3 sm:mb-6 text-xs sm:text-sm">
            <div className="flex items-center">
              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-green-100 border border-green-300 rounded mr-1 sm:mr-2"></div>
              <span>Available</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-blue-100 border border-blue-300 rounded mr-1 sm:mr-2"></div>
              <span>B2C</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-orange-100 border border-orange-400 rounded mr-1 sm:mr-2"></div>
              <span>B2B</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-purple-100 border border-purple-400 rounded mr-1 sm:mr-2"></div>
              <span>Event</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-yellow-100 border border-yellow-300 rounded mr-1 sm:mr-2"></div>
              <span>Pending</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-red-100 border border-red-300 rounded mr-1 sm:mr-2"></div>
              <span>Blocked</span>
            </div>
          </div>

          {/* No courts warning */}
          {sections.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg font-medium">No courts configured for this facility</p>
              <p className="text-sm mt-2">Please configure courts in the facility settings</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-500">Loading availability...</span>
            </div>
          ) : sections.length > 0 && (
            /* Calendar Table - Mobile Optimized */
            <div className="overflow-x-auto overflow-y-auto max-h-[60vh] sm:max-h-[70vh] -mx-2 sm:mx-0" key={`calendar-wrapper-${selectedDate}`}>
              <table className="w-full border-collapse min-w-max" key={`calendar-${selectedDate}-${slotAvailability?.date || 'loading'}`}>
                <thead className="sticky top-0 z-20 bg-white shadow-sm">
                  <tr>
                    <th className="sticky left-0 bg-white z-30 p-1 sm:p-2 border-b font-semibold text-left text-xs sm:text-sm min-w-[50px] sm:min-w-[70px]">
                      Time
                    </th>
                    {sections.map((section) => (
                      <th 
                        key={section} 
                        className="p-1 sm:p-2 border-b font-semibold text-center text-[10px] sm:text-sm min-w-[45px] sm:min-w-[120px] bg-white"
                        title={sectionNames[section]}
                      >
                        {/* Show short name on mobile, full name on desktop */}
                        <span className="sm:hidden">{getShortCourtName(section, sectionNames[section])}</span>
                        <span className="hidden sm:inline">{sectionNames[section] || section}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(!slotAvailability || loading) ? (
                    <tr>
                      <td colSpan={sections.length + 1} className="text-center py-8 text-gray-500">
                        Loading availability...
                      </td>
                    </tr>
                  ) : TIME_SLOTS.map((time) => (
                    <tr key={time}>
                      <td className="sticky left-0 bg-white z-10 p-1 sm:p-2 border-b text-[10px] sm:text-sm font-medium whitespace-nowrap">
                        {/* Short time on mobile */}
                        <span className="sm:hidden">{formatTimeShort(time)}</span>
                        <span className="hidden sm:inline">{formatTime(time)}</span>
                      </td>
                      {sections.map((section) => {
                        const { status, booking, isBlockedByOther } = getSlotStatus(selectedDate, time, section);
                        const isInDragSelection = isSlotInDragSelection(time, section);
                        
                        // Handle booking slot display with overnight support
                        let startIndex = booking ? TIME_SLOTS.indexOf(booking.startTime) : -1;
                        let endIndex = booking ? TIME_SLOTS.indexOf(booking.endTime) : -1;
                        const currentIndex = TIME_SLOTS.indexOf(time);
                        
                        // Handle edge cases for overnight bookings
                        // If endTime is not in TIME_SLOTS (e.g., "02:00" when slots go to "01:30"), 
                        // find the last slot that's before the end time
                        if (booking && endIndex === -1 && booking.endTime) {
                          // Find the closest slot before the end time
                          const endHour = parseInt(booking.endTime.split(':')[0]);
                          const endMin = parseInt(booking.endTime.split(':')[1]);
                          const endMinutes = endHour * 60 + endMin;
                          
                          for (let i = TIME_SLOTS.length - 1; i >= 0; i--) {
                            const slotHour = parseInt(TIME_SLOTS[i].split(':')[0]);
                            const slotMin = parseInt(TIME_SLOTS[i].split(':')[1]);
                            const slotMinutes = slotHour * 60 + slotMin;
                            
                            // For early morning end times (0-4 AM), look in early morning slots
                            if (endHour < 5 && slotHour < 5 && slotMinutes < endMinutes) {
                              endIndex = i + 1;
                              break;
                            }
                          }
                          // If still not found, use last early morning slot + 1
                          if (endIndex === -1 && endHour < 5) {
                            for (let i = 0; i < TIME_SLOTS.length; i++) {
                              const slotHour = parseInt(TIME_SLOTS[i].split(':')[0]);
                              if (slotHour >= 5) {
                                endIndex = i;
                                break;
                              }
                            }
                          }
                        }
                        
                        // Handle bookings that cross midnight (e.g., 22:00 to 00:00)
                        // In overnight arrays, early slots (00:00-01:30) come BEFORE late slots (05:00-23:30)
                        // So if endIndex < startIndex, it means the booking crosses from evening to early morning
                        let bookingSlotCount = 1;
                        let isPartOfBooking = false;
                        
                        if (booking && startIndex !== -1) {
                          if (endIndex !== -1 && endIndex > startIndex) {
                            // Normal case: booking within same section of day
                            bookingSlotCount = endIndex - startIndex;
                            isPartOfBooking = currentIndex >= startIndex && currentIndex < endIndex;
                          } else if (endIndex !== -1 && endIndex <= startIndex) {
                            // Overnight booking: ends in early morning (which comes first in array)
                            // Booking spans from startIndex to end of array, then 0 to endIndex
                            const slotsToEndOfDay = TIME_SLOTS.length - startIndex;
                            bookingSlotCount = slotsToEndOfDay + endIndex;
                            isPartOfBooking = currentIndex >= startIndex || currentIndex < endIndex;
                          } else if (endIndex === -1) {
                            // End time not found, show at least one slot
                            bookingSlotCount = 1;
                            isPartOfBooking = currentIndex === startIndex;
                          }
                        }
                        
                        // Calculate middle slot for displaying booking info
                        let middleSlotIndex = startIndex;
                        if (bookingSlotCount > 1) {
                          if (endIndex > startIndex) {
                            middleSlotIndex = startIndex + Math.floor(bookingSlotCount / 2);
                          } else if (endIndex <= startIndex && endIndex !== -1) {
                            // For overnight bookings, put info in the evening portion
                            const eveningSlots = TIME_SLOTS.length - startIndex;
                            middleSlotIndex = startIndex + Math.floor(eveningSlots / 2);
                          }
                        }
                        
                        const isMiddleSlot = booking && currentIndex === middleSlotIndex;
                        
                        // Determine if this is the last slot of the booking
                        let isLastSlotOfBooking = false;
                        if (booking && isPartOfBooking) {
                          if (endIndex > startIndex) {
                            isLastSlotOfBooking = currentIndex === endIndex - 1;
                          } else if (endIndex <= startIndex && endIndex !== -1) {
                            // For overnight, last slot is endIndex - 1 (in early morning)
                            isLastSlotOfBooking = currentIndex === endIndex - 1 || 
                              (endIndex === 0 && currentIndex === TIME_SLOTS.length - 1);
                          }
                        }
                        
                        const shouldRemoveBottomBorder = booking && isPartOfBooking && !isLastSlotOfBooking;

                        return (
                          <td
                            key={`${time}-${section}-${slotAvailability?.date || 'x'}`}
                            data-testid={`calendar-cell-${time}-${section}`}
                            data-slot-status={status}
                            className={`border-l cursor-pointer transition-colors select-none relative ${
                              shouldRemoveBottomBorder ? '' : 'border-b'
                            } ${getStatusColor(status, booking, isInDragSelection, isBlockedByOther)}`}
                            style={{ height: '32px', verticalAlign: 'middle' }}
                            onMouseDown={() => handleSlotMouseDown(selectedDate, time, section, status)}
                            onMouseEnter={() => handleSlotMouseEnter(time, section)}
                            onMouseUp={() => handleSlotMouseUp(selectedDate, time, section, status)}
                            onTouchStart={(e) => handleTouchStart(e, selectedDate, time, section, status)}
                            onTouchEnd={(e) => handleTouchEnd(e, selectedDate, time, section, status)}
                          >
                            {isMiddleSlot && !isBlockedByOther && (
                              <div className="text-[8px] sm:text-xs flex flex-col justify-center items-center px-0.5 sm:px-1">
                                <div className="font-semibold truncate w-full text-center">
                                  {/* Shorter name on mobile */}
                                  <span className="sm:hidden">{booking.customerName?.split(' ')[0] || 'Booked'}</span>
                                  <span className="hidden sm:inline">{booking.customerName}</span>
                                </div>
                                <div className="text-gray-600 truncate w-full text-center hidden sm:block text-[10px]">
                                  {booking.customerPhone}
                                </div>
                                {/* Time slot */}
                                <div className="text-gray-500 text-[8px] sm:text-[10px] mt-0.5">
                                  {booking.startTime} - {booking.endTime}
                                </div>
                                {/* Booking Source Badge (replaced booking type) */}
                                <div className="flex items-center justify-center gap-0.5 sm:gap-1 mt-0.5 sm:mt-1 flex-wrap">
                                  {booking.source && (
                                    <span className={`px-0.5 sm:px-1 py-0.5 rounded text-[7px] sm:text-[10px] font-semibold ${
                                      booking.source === 'playo' ? 'bg-green-200 text-green-800' :
                                      booking.source === 'hudle' ? 'bg-purple-200 text-purple-800' :
                                      booking.source === 'online' ? 'bg-blue-200 text-blue-800' :
                                      'bg-gray-200 text-gray-700'
                                    }`}>
                                      {booking.source === 'direct' ? 'Admin' : booking.source.charAt(0).toUpperCase() + booking.source.slice(1)}
                                    </span>
                                  )}
                                </div>
                                {/* Payment Status */}
                                {booking.paymentStatus === 'paid' && (
                                  <div className="text-green-600 font-semibold text-[7px] sm:text-[10px] mt-0.5">✓ Paid</div>
                                )}
                                {booking.paymentStatus === 'partial' && (
                                  <div className="text-orange-600 font-semibold text-[7px] sm:text-[10px] mt-0.5">
                                    Due: ₹{booking.amountPending || (booking.amount - booking.amountPaid)}
                                  </div>
                                )}
                                {booking.paymentStatus === 'pending' && (
                                  <div className="text-red-600 font-semibold text-[7px] sm:text-[10px] mt-0.5">
                                    Due: ₹{booking.amount || 0}
                                  </div>
                                )}
                                {/* Payment-link status badge (so staff see link state at a glance) */}
                                {booking.paymentLinkSent && (
                                  <div
                                    data-testid={`payment-link-badge-${booking.id}`}
                                    className={`inline-block px-1 py-0.5 rounded text-[7px] sm:text-[9px] font-bold mt-0.5 ${
                                      booking.paymentStatus === 'paid'
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-blue-100 text-blue-700'
                                    }`}
                                  >
                                    {booking.paymentStatus === 'paid' ? '🔗 Link paid' : '🔗 Link sent'}
                                  </div>
                                )}
                              </div>
                            )}
                            {isBlockedByOther && isMiddleSlot && (
                              <div className="text-[8px] sm:text-xs text-gray-500 font-medium text-center">
                                <span className="sm:hidden">-</span>
                                <span className="hidden sm:inline">Blocked</span>
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Single Slot Booking Dialog */}
      {showBookingDialog && (
        <ErrorBoundary label="BookingDialog (CalendarView)">
          <BookingDialog
            isOpen={showBookingDialog}
            onClose={handleDialogClose}
            slotInfo={selectedSlot}
            facility={facility}
          />
        </ErrorBoundary>
      )}

      {/* Multi-Slot Booking Dialog */}
      {showMultiSlotDialog && (
        <ErrorBoundary label="MultiSlotBookingDialog (CalendarView)">
          <MultiSlotBookingDialog
            isOpen={showMultiSlotDialog}
            onClose={handleDialogClose}
            slotInfo={selectedSlots}
            facility={facility}
          />
        </ErrorBoundary>
      )}

      {/* Date Picker Dialog */}
      {showDatePicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-sm w-full p-4 sm:p-6">
            <h3 className="text-lg font-bold mb-4">Choose Date</h3>
            <input
              type="date"
              className="w-full p-3 border rounded-lg text-lg cursor-pointer"
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                setShowDatePicker(false);
              }}
            />
            <div className="flex space-x-2 mt-4">
              <Button variant="outline" onClick={() => setShowDatePicker(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  setSelectedDate(new Date().toISOString().split('T')[0]);
                  setShowDatePicker(false);
                }} 
                className="flex-1"
              >
                Today
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
